import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User } from 'lucide-react';
import { differenceInDays } from 'date-fns';
import PropTypes from 'prop-types';
import AppointmentService from '../../services/appoinment.service';
import { getStatusLabel, getStatusColor } from '../../utils/appointmentUtils';
import {
  formatDate,
  formatTime,
} from '../../utils/dateUtils';

const AdminPatientAppointments = ({ patientId }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAppointments();
  }, [patientId]);

  const loadAppointments = async () => {
    console.log("=== [DEBUG] Loading appointments for patient ID:", patientId);
    try {
      setLoading(true);
      const patientAppointments = await AppointmentService.getAdminAppointments({ 
          patient_id: patientId 
      });
      console.log("=== [DEBUG] Appointments received:", patientAppointments);
      setAppointments(patientAppointments);
    } catch (err) {
      console.error('Errore durante il caricamento degli appuntamenti:', err);
      setError('Impossibile caricare gli appuntamenti. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (appointment) => {
    const appointmentDate = appointment.date; // ✅ CORRETTO: usa "date" non "appointment_date"
    const daysRemaining = differenceInDays(new Date(appointmentDate), new Date());
    const statusLabel = getStatusLabel(appointment.status, daysRemaining);
    const statusColor = getStatusColor(appointment.status, daysRemaining);

    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>{statusLabel}</span>;
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-4 border-purple-500 rounded-full border-t-transparent"></div></div>;
  }

  if (error) {
    return <div className="bg-red-50 p-4 rounded-md text-red-700">{error}</div>;
  }

  if (appointments.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg text-center">
        <p className="text-gray-500">Nessun appuntamento trovato per questo paziente.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        {appointments.map((appointment) => {
          // ✅ CORRETTO: usa i dati già presenti nel JSON
          const therapist = appointment.therapist?.user || {};
          
          return (
            <div key={appointment.id} className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-medium">{appointment.service_name || 'Appuntamento'}</h3>
                  <div className="flex items-center mt-1 text-gray-600">
                    <User className="w-4 h-4 mr-2" />
                    <span>
                      {therapist.first_name} {therapist.last_name}
                      {appointment.therapist?.specialization ? ` - ${appointment.therapist.specialization}` : ''}
                    </span>
                  </div>
                </div>
                {getStatusBadge(appointment)}
              </div>

              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  {/* ✅ CORRETTO: usa "date" */}
                  <span>{formatDate(appointment.date)}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-2" />
                  {/* ✅ CORRETTO: usa "time" */}
                  <span>{formatTime(appointment.time)}</span>
                </div>
              </div>

              {/* Informazioni aggiuntive */}
              <div className="mt-3 pt-3 border-t text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">Prezzo:</span> {appointment.price} €
                  </div>
                  <div>
                    <span className="font-medium">Status:</span> {appointment.status}
                  </div>
                </div>
              </div>

              {appointment.feedback && (
                <div className="mt-3 pt-3 border-t">
                  <h4 className="text-sm font-medium mb-1">Feedback del paziente:</h4>
                  <p className="text-sm text-gray-600">{appointment.feedback}</p>
                  {appointment.feedback_rating && (
                    <div className="mt-1 flex items-center">
                      <span className="text-sm text-gray-500 mr-2">Valutazione:</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-4 h-4 ${i < appointment.feedback_rating ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

AdminPatientAppointments.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default AdminPatientAppointments;